import { ScreenBody } from '@/components/ScreenBody';
import { AppContent, AppSection, AppStack } from '@/components/layout/AppLayout';
import { Badge } from '@/components/shadcn-ui/Badge';
import { Progress } from '@/components/shadcn-ui/Progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/shadcn-ui/Tabs';
import { useTaxiData } from '@/contexts/TaxiDataContext';
import { buildDailyMonthlyAnalytics } from '@/lib/taxiAnalytics';
import {
	Activity,
	BarChart3,
	CalendarDays,
	CircleDollarSign,
	Clock3,
	Flame,
	MapPin,
	TrendingUp,
} from 'lucide-react';
import { useMemo } from 'react';
import { Bar, Line } from 'react-chartjs-2';
import {
	BarElement,
	CategoryScale,
	Chart as ChartJS,
	Legend,
	LineElement,
	LinearScale,
	PointElement,
	Tooltip,
} from 'chart.js';

ChartJS.register(
	CategoryScale,
	LinearScale,
	PointElement,
	LineElement,
	BarElement,
	Legend,
	Tooltip,
);

const formatCurrency = (value: number) => `¥${Math.round(value).toLocaleString()}`;
const formatPercent = (value: number) => `${Math.round(value * 100)}%`;

const DeltaBadge = ({ delta }: { delta: number }) => {
	const isPositive = delta >= 0;
	return (
		<Badge className={isPositive ? 'bg-emerald-100 text-emerald-700' : 'bg-rose-100 text-rose-700'}>
			{isPositive ? '+' : ''}
			{delta}
		</Badge>
	);
};

const MetricCard = ({
	label,
	today,
	average,
	unit = '',
}: {
	label: string;
	today: number;
	average: number;
	unit?: string;
}) => {
	const delta = today - average;
	return (
		<article className="rounded-xl border p-3">
			<div className="text-xs text-slate-500">{label}</div>
			<div className="mt-1 flex items-end justify-between">
				<div className="text-lg font-semibold">
					{Math.round(today).toLocaleString()}
					{unit}
				</div>
				<DeltaBadge delta={Math.round(delta)} />
			</div>
			<div className="mt-1 text-[11px] text-slate-500">
				30日平均: {Math.round(average).toLocaleString()}
				{unit}
			</div>
		</article>
	);
};

const TaxiAnalyticsDashboard = () => {
	const { logs, gpsSessions } = useTaxiData();
	const analytics = useMemo(
		() => buildDailyMonthlyAnalytics(logs, gpsSessions),
		[logs, gpsSessions],
	);

	const trendLineData = useMemo(
		() => ({
			labels: analytics.dailyTrend.map((item) => item.dateKey.slice(5)),
			datasets: [
				{
					label: '売上',
					data: analytics.dailyTrend.map((item) => item.revenue),
					borderColor: '#2563eb',
					backgroundColor: 'rgba(37, 99, 235, 0.2)',
					tension: 0.25,
					yAxisID: 'y',
				},
				{
					label: 'スコア',
					data: analytics.dailyTrend.map((item) => item.score),
					borderColor: '#f97316',
					backgroundColor: 'rgba(249, 115, 22, 0.2)',
					tension: 0.25,
					yAxisID: 'y1',
				},
			],
		}),
		[analytics.dailyTrend],
	);

	const trendLineOptions = useMemo(
		() => ({
			responsive: true,
			maintainAspectRatio: false,
			interaction: { mode: 'index' as const, intersect: false },
			plugins: { legend: { position: 'bottom' as const } },
			scales: {
				y: {
					type: 'linear' as const,
					position: 'left' as const,
					ticks: {
						callback: (value: unknown) => `¥${Number(value).toLocaleString()}`,
					},
				},
				y1: {
					type: 'linear' as const,
					position: 'right' as const,
					grid: { drawOnChartArea: false },
					min: 0,
					max: 100,
				},
			},
		}),
		[],
	);

	const heatmapBars = useMemo(() => {
		const source = analytics.hourlyHeatmap30d.filter(
			(item) => item.pickups > 0 || item.revenue > 0,
		);
		return {
			labels: source.map((item) => `${item.hour}時`),
			datasets: [
				{
					label: '売上',
					data: source.map((item) => item.revenue),
					backgroundColor: 'rgba(59, 130, 246, 0.7)',
				},
				{
					label: '乗車件数',
					data: source.map((item) => item.pickups),
					backgroundColor: 'rgba(16, 185, 129, 0.7)',
					yAxisID: 'y1',
				},
			],
		};
	}, [analytics.hourlyHeatmap30d]);

	return (
		<ScreenBody>
			<AppContent>
				<AppStack className="pb-3">
					<AppSection>
						<div className="flex items-start justify-between gap-3">
							<div>
								<h1 className="flex items-center gap-2 text-lg font-bold">
									<BarChart3 className="h-5 w-5 text-slate-600" />
									分析ダッシュボード
								</h1>
								<p className="text-xs text-slate-600">
									JST固定 / 直近30日ローリング比較
								</p>
								<p className="mt-1 text-[11px] text-slate-500">
									今日: {analytics.todayKey}
								</p>
							</div>
							<div className="flex items-center gap-2">
								<div className="text-right">
									<div className="text-xs text-slate-500">今日スコア</div>
									<div className="text-xl font-semibold">{analytics.today.score}</div>
								</div>
								<DeltaBadge delta={analytics.deltaScore} />
							</div>
						</div>
						<div className="mt-3 space-y-1">
							<div className="flex items-center justify-between text-xs text-slate-500">
								<span>30日平均比</span>
								<span>{analytics.today.score} / 100</span>
							</div>
							<Progress value={analytics.today.score} />
						</div>
					</AppSection>

					<Tabs defaultValue="today">
						<TabsList className="grid w-full grid-cols-2">
							<TabsTrigger value="today" className="gap-1">
								<CalendarDays className="h-4 w-4" />
								今日
							</TabsTrigger>
							<TabsTrigger value="month" className="gap-1">
								<TrendingUp className="h-4 w-4" />
								過去30日
							</TabsTrigger>
						</TabsList>

						<TabsContent value="today">
							<AppStack>
								<AppSection>
									<h2 className="mb-2 flex items-center gap-2 font-semibold">
										<Flame className="h-4 w-4 text-orange-500" />
										今日の結果 vs 30日平均
									</h2>
									<div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
										<article className="rounded-xl border p-3">
											<div className="text-xs text-slate-500">今日の売上</div>
											<div className="text-xl font-semibold">
												{formatCurrency(analytics.today.revenue)}
											</div>
											<div className="mt-1 text-[11px] text-slate-500">
												30日平均: {formatCurrency(analytics.last30dAverage.revenue)}
											</div>
										</article>
										<article className="rounded-xl border p-3">
											<div className="text-xs text-slate-500">差分</div>
											<div className="flex items-center gap-2 text-xl font-semibold">
												{formatCurrency(analytics.deltaRevenue)}
												<DeltaBadge delta={analytics.deltaRevenue} />
											</div>
											<div className="mt-1 text-[11px] text-slate-500">
												プラスなら本日の戦略は有効
											</div>
										</article>
									</div>
									<div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
										<MetricCard
											label="乗車件数"
											today={analytics.today.pickups}
											average={analytics.last30dAverage.pickups}
											unit="件"
										/>
										<MetricCard
											label="平均客単価"
											today={analytics.today.avgFare}
											average={analytics.last30dAverage.avgFare}
											unit="円"
										/>
										<MetricCard
											label="時間当たり売上"
											today={analytics.today.perHourRevenue}
											average={analytics.last30dAverage.perHourRevenue}
											unit="円/h"
										/>
										<MetricCard
											label="空車時間率"
											today={analytics.today.idleRatio * 100}
											average={analytics.last30dAverage.idleRatio * 100}
											unit="%"
										/>
									</div>
								</AppSection>

								<AppSection>
									<h2 className="mb-2 flex items-center gap-2 font-semibold">
										<Activity className="h-4 w-4 text-slate-500" />
										今日の行動品質
									</h2>
									<div className="grid grid-cols-2 gap-2 text-sm">
										<div className="rounded-lg border p-2">
											<div className="text-xs text-slate-500">待機→乗車転換率</div>
											<div className="font-semibold">
												{formatPercent(analytics.today.captureRate)}
											</div>
										</div>
										<div className="rounded-lg border p-2">
											<div className="text-xs text-slate-500">待機時移動距離</div>
											<div className="font-semibold">{analytics.today.cruiseKm} km</div>
										</div>
										<div className="rounded-lg border p-2">
											<div className="text-xs text-slate-500">待機時間</div>
											<div className="font-semibold">{analytics.today.idleMinutes} 分</div>
										</div>
										<div className="rounded-lg border p-2">
											<div className="text-xs text-slate-500">降車件数</div>
											<div className="font-semibold">{analytics.today.dropoffs} 件</div>
										</div>
									</div>
								</AppSection>
							</AppStack>
						</TabsContent>

						<TabsContent value="month">
							<AppStack>
								<AppSection>
									<h2 className="mb-2 flex items-center gap-2 font-semibold">
										<CircleDollarSign className="h-4 w-4 text-blue-600" />
										日次トレンド（売上 / スコア）
									</h2>
									<div className="h-64">
										<Line data={trendLineData} options={trendLineOptions} />
									</div>
								</AppSection>

								<AppSection>
									<h2 className="mb-2 flex items-center gap-2 font-semibold">
										<Clock3 className="h-4 w-4 text-slate-500" />
										時間帯別（過去30日）
									</h2>
									<div className="h-64">
										<Bar
											data={heatmapBars}
											options={{
												responsive: true,
												maintainAspectRatio: false,
												plugins: {
													legend: { position: 'bottom' as const },
												},
												scales: {
													y: {
														ticks: {
															callback: (value: unknown) =>
																`¥${Number(value).toLocaleString()}`,
														},
													},
													y1: {
														position: 'right' as const,
														grid: { drawOnChartArea: false },
													},
												},
											}}
										/>
									</div>
								</AppSection>

								<AppSection>
									<h2 className="mb-2 flex items-center gap-2 font-semibold">
										<MapPin className="h-4 w-4 text-slate-500" />
										高収益エリアランキング（過去30日）
									</h2>
									{analytics.topZones30d.length === 0 ? (
										<p className="text-sm text-slate-500">
											zone:新宿駅東口 のようにメモにエリアを記録すると、分析精度が上がります
										</p>
									) : (
										<ul className="space-y-2 text-sm">
											{analytics.topZones30d.map((zone, index) => (
												<li key={zone.zone} className="rounded-xl border p-3">
													<div className="font-semibold">
														#{index + 1} {zone.zone}
													</div>
													<div className="text-xs text-slate-600">
														乗車 {zone.pickups}件 / 累計 {formatCurrency(zone.totalRevenue)} /
														平均 {formatCurrency(zone.avgRevenue)}
													</div>
												</li>
											))}
										</ul>
									)}
								</AppSection>
							</AppStack>
						</TabsContent>
					</Tabs>
				</AppStack>
			</AppContent>
		</ScreenBody>
	);
};

export default TaxiAnalyticsDashboard;
